import * as v from "valibot";
import { WebSocket as PartySocket } from "partysocket";
import { RealtimeEventJsonSchema } from "../../shared/schemas";
import type { ClientEvent, RealtimeEvent } from "../../shared/types";
export type ClientConnectionEvent =
  | RealtimeEvent
  | { type: "connection"; connected: boolean };
class RealtimeClient {
  private socket: PartySocket | null = null;
  private userId: string | null = null;
  private listeners = new Set<(event: ClientConnectionEvent) => void>();
  private ping: ReturnType<typeof setInterval> | null = null;
  get connected() {
    return this.socket?.readyState === PartySocket.OPEN;
  }
  subscribe(listener: (event: ClientConnectionEvent) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  private emit(event: ClientConnectionEvent) {
    for (const listener of this.listeners) listener(event);
  }
  connect(userId: string) {
    if (this.userId === userId && this.socket) return;
    this.disconnect();
    this.userId = userId;
    const url = new URL("/ws", location.href);
    url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new PartySocket(url.href, [], {
      minReconnectionDelay: 500 + Math.random() * 250,
      maxReconnectionDelay: 8000,
      reconnectionDelayGrowFactor: 2,
      // ICE and call liveness must never be replayed after an outage.
      maxEnqueuedMessages: 0,
      shouldReconnectOnClose: (event) => event.code !== 4001,
    });
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.stopPing();
      this.ping = setInterval(() => {
        if (this.socket === socket && this.connected) socket.send("ping");
      }, 25000);
      this.emit({ type: "connection", connected: true });
    });
    socket.addEventListener("message", (event) => {
      if (this.socket !== socket || event.data === "pong") return;
      const result = v.safeParse(RealtimeEventJsonSchema, event.data);
      if (result.success) {
        if (result.output.type === "session-ended") this.disconnect();
        this.emit(result.output);
      }
    });
    socket.addEventListener("close", (event) => {
      if (this.socket !== socket) return;
      this.stopPing();
      if (event.code === 4001) {
        this.disconnect();
        this.emit({ type: "session-ended" });
        return;
      }
      this.emit({ type: "connection", connected: false });
    });
  }
  private stopPing() {
    if (this.ping) clearInterval(this.ping);
    this.ping = null;
  }
  disconnect() {
    this.userId = null;
    this.stopPing();
    const socket = this.socket;
    this.socket = null;
    socket?.close(1000, "Leaving");
    this.emit({ type: "connection", connected: false });
  }
  send(event: ClientEvent) {
    if (!this.connected) return false;
    try {
      return this.socket!.send(JSON.stringify(event));
    } catch {
      return false;
    }
  }
}
export const realtime = new RealtimeClient();
